import { useRef, useState } from "react";
import { Upload, Trash2, Loader2, Image as ImageIcon, Film, Pencil } from "lucide-react";
import { uploadFile } from "../lib/upload";
import api, { BACKEND_URL, formatApiError } from "../lib/api";
import { toast } from "sonner";
import MediaEditor from "./editor/MediaEditor";

const ACCEPT = "image/*,video/mp4,video/webm,video/quicktime";

function toAbsolute(url) {
  if (!url) return url;
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}

function isVideo(url = "") {
  return /\.(mp4|webm|mov)$/i.test(url);
}

export default function MediaUploader({ items = [], onChange, max = 12, testId = "media-uploader" }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [editorFile, setEditorFile] = useState(null);
  const inputRef = useRef(null);

  const uploadOneFile = async (f) => {
    const data = await uploadFile(f);
    return data.url;
  };

  const handleFilesSelected = (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const room = Math.max(0, max - items.length);
    const toSend = list.slice(0, room);
    if (list.length > room) toast.warning(`Only added ${room} — max ${max} items`);
    if (toSend.length === 0) return;
    // Queue them and open editor for the first
    setPendingFiles(toSend);
    setEditorFile(toSend[0]);
  };

  const skipEditAndUpload = async (file) => {
    setUploading(true);
    try {
      const url = await uploadOneFile(file);
      onChange([...(items || []), url]);
    } catch (e) {
      toast.error(`${file.name}: ${formatApiError(e.response?.data?.detail) || "upload failed"}`);
    } finally {
      setUploading(false);
    }
  };

  const onEditorExport = async (exportedFile) => {
    // Upload the edited file, then advance to the next pending
    setEditorFile(null);
    setUploading(true);
    try {
      const url = await uploadOneFile(exportedFile);
      onChange([...(items || []), url]);
    } catch (e) {
      toast.error(`Upload failed: ${formatApiError(e.response?.data?.detail) || "unknown error"}`);
    } finally {
      setUploading(false);
    }
    // Next file
    const remaining = pendingFiles.slice(1);
    setPendingFiles(remaining);
    if (remaining.length > 0) setTimeout(() => setEditorFile(remaining[0]), 200);
  };

  const onEditorCancel = async () => {
    // User skipped editing → upload the original as-is
    const current = editorFile;
    setEditorFile(null);
    if (current) await skipEditAndUpload(current);
    const remaining = pendingFiles.slice(1);
    setPendingFiles(remaining);
    if (remaining.length > 0) setTimeout(() => setEditorFile(remaining[0]), 200);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFilesSelected(e.dataTransfer.files);
  };

  const remove = (i) => {
    const next = items.filter((_, idx) => idx !== i);
    onChange(next);
  };

  return (
    <div className="space-y-4" data-testid={testId}>
      {editorFile && (
        <MediaEditor
          file={editorFile}
          onExport={onEditorExport}
          onCancel={onEditorCancel}
        />
      )}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        className={`relative rounded-2xl border-2 border-dashed p-8 text-center transition-colors cursor-pointer ${
          dragOver ? "border-nexus-purple bg-nexus-purple/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"
        }`}
        data-testid={`${testId}-dropzone`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => handleFilesSelected(e.target.files)}
          data-testid={`${testId}-input`}
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-3 text-zinc-300">
            <Loader2 className="h-5 w-5 animate-spin text-nexus-purple" />
            <span>Uploading…</span>
          </div>
        ) : (
          <>
            <Upload className="h-6 w-6 mx-auto mb-3 text-nexus-blue" />
            <div className="font-semibold">Drop images or short videos</div>
            <div className="text-xs text-zinc-500 mt-1">
              JPG · PNG · WebP · MP4 · WebM · up to 20 MB · max {max} items
            </div>
            <div className="text-[10px] uppercase tracking-widest text-nexus-pink/80 mt-2 flex items-center justify-center gap-1.5">
              <Pencil className="h-2.5 w-2.5" /> Edit in Studio before upload
            </div>
          </>
        )}
      </div>

      {items.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid={`${testId}-grid`}>
          {items.map((u, i) => {
            const abs = toAbsolute(u);
            const video = isVideo(u);
            return (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden hairline group bg-black" data-testid={`${testId}-item-${i}`}>
                {video ? (
                  <video src={abs} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                ) : (
                  <img src={abs} alt="" className="w-full h-full object-cover" />
                )}
                <div className="absolute top-2 left-2 glass px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest flex items-center gap-1">
                  {video ? <Film className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                  {video ? "Video" : "Image"}
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); remove(i); }}
                  className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-nexus-pink/60"
                  data-testid={`${testId}-remove-${i}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
